import { Phone, Mail, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import logo from "@/assets/onyx-logo-small.webp";

const serviceLinks = [
  { label: "Emergency Plumbing Repair", href: "/services/emergency" },
  { label: "Water Heater Installation & Repair", href: "/services/water-heaters" },
  { label: "Drain Cleaning & Sewer Clearing", href: "/services/drain-cleaning" },
  { label: "Kitchen & Bathroom Remodeling", href: "/services/remodeling" },
  { label: "Residential Plumbing", href: "/services/residential" },
  { label: "Commercial Plumbing", href: "/services/commercial" },
];

const quickLinks = [
  { label: "Home", href: "/" },
  { label: "About Us", href: "/about" },
  { label: "All Services", href: "/services" },
  { label: "Service Areas", href: "/service-areas" },
  { label: "Memberships", href: "/memberships" },
  { label: "Reviews", href: "/reviews" },
  { label: "Contact", href: "/contact" },
];

const cityLinks = [
  { label: "Gulf Shores Plumber", href: "/gulf-shores-plumber", title: "Emergency plumber in Gulf Shores AL" },
  { label: "Orange Beach Plumber", href: "/orange-beach-plumber", title: "Plumber in Orange Beach AL" },
  { label: "Foley Plumber", href: "/foley-plumber", title: "Plumbing services in Foley AL" },
  { label: "Fairhope Plumber", href: "/fairhope-plumber", title: "Plumber in Fairhope AL" },
  { label: "Daphne Plumber", href: "/daphne-plumber", title: "Plumber in Daphne AL" },
  { label: "Spanish Fort Plumber", href: "/spanish-fort-plumber", title: "Plumber in Spanish Fort AL" },
  { label: "Robertsdale Plumber", href: "/robertsdale-plumber", title: "Plumber in Robertsdale AL" },
  { label: "Loxley Plumber", href: "/loxley-plumber", title: "Plumber in Loxley AL" },
  { label: "Summerdale Plumber", href: "/summerdale-plumber", title: "Plumber in Summerdale AL" },
  { label: "Bay Minette Plumber", href: "/bay-minette-plumber", title: "Plumber in Bay Minette AL" },
  { label: "Elberta Plumber", href: "/elberta-plumber", title: "Plumber in Elberta AL" },
  { label: "Perdido Beach Plumber", href: "/perdido-beach-plumber", title: "Plumber in Perdido Beach AL" },
  { label: "Magnolia Springs Plumber", href: "/magnolia-springs-plumber", title: "Plumber in Magnolia Springs AL" },
];

export const Footer = () => {
  return (
    <footer className="bg-onyx border-t border-border" itemScope itemType="https://schema.org/Plumber">
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* NAP Block - Critical for Local SEO */}
          <div className="lg:col-span-1">
            <img src={logo} alt="Onyx Professional Plumbing - Licensed Plumber Gulf Shores Baldwin County AL" className="h-12 w-auto mb-4" width="129" height="48" />
            <address className="not-italic text-sm text-muted-foreground space-y-1 mb-4">
              <p className="font-semibold text-foreground" itemProp="name">Onyx Professional Plumbing</p>
              <p itemProp="address" itemScope itemType="https://schema.org/PostalAddress">
                <span itemProp="streetAddress">6642 AL-59 Unit D-3</span><br />
                <span itemProp="addressLocality">Gulf Shores</span>, <span itemProp="addressRegion">AL</span> <span itemProp="postalCode">36542</span>
              </p>
              <p>
                Phone:{" "}
                <a href="tel:251-504-4452" className="text-primary hover:text-primary/80 transition-colors font-semibold" itemProp="telephone">
                  (251) 504-4452
                </a>
              </p>
              <p>
                Email:{" "}
                <a href="mailto:onyxproplumbing@gmail.com" className="hover:text-primary transition-colors" itemProp="email">
                  onyxproplumbing@gmail.com
                </a>
              </p>
              <p className="font-medium text-foreground/80">License: AL MP-4174</p>
            </address>
            <p className="text-xs text-muted-foreground/80 leading-relaxed">
              Available 24/7 for Emergency Plumbing
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-foreground mb-4">Quick Links</h4>
            <nav className="flex flex-col gap-2" aria-label="Quick navigation">
              {quickLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Services with keyword-rich anchor text */}
          <div>
            <h4 className="font-semibold text-foreground mb-4">Our Services</h4>
            <nav className="flex flex-col gap-2" aria-label="Services">
              {serviceLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-muted-foreground hover:text-primary transition-colors text-sm"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-semibold text-foreground mb-4">Contact Us</h4>
            <div className="space-y-3">
              <a
                href="tel:251-504-4452"
                className="flex items-center gap-3 text-muted-foreground hover:text-primary transition-colors text-sm"
              >
                <Phone className="h-4 w-4 text-primary" />
                (251) 504-4452
              </a>
              <a
                href="mailto:onyxproplumbing@gmail.com"
                className="flex items-center gap-3 text-muted-foreground hover:text-primary transition-colors text-sm"
              >
                <Mail className="h-4 w-4 text-primary" />
                onyxproplumbing@gmail.com
              </a>
              <a
                href="https://maps.google.com/?q=6642+AL-59+Unit+D-3+Gulf+Shores+AL+36542"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 text-muted-foreground hover:text-primary transition-colors text-sm"
              >
                <MapPin className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                <span>
                  6642 AL-59 Unit D-3<br />
                  Gulf Shores, AL 36542
                </span>
              </a>
            </div>
          </div>
        </div>

        {/* Service Areas - Full city links for SEO */}
        <div className="border-t border-border mt-12 pt-8">
          <h5 className="text-center text-sm font-semibold text-foreground mb-6">
            Serving Gulf Shores, Foley, Orange Beach, Fairhope, Daphne, Spanish Fort, Ono Island & All of Baldwin County, AL
          </h5>
          <nav className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 text-sm" aria-label="Service areas">
            {cityLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                title={link.title}
                className="text-muted-foreground hover:text-primary transition-colors text-center py-1"
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-border mt-8 pt-8 text-center space-y-2">
          <p className="text-muted-foreground text-sm">
            © {new Date().getFullYear()} Onyx Professional Plumbing. Licensed (MP-4174) & Insured Plumber in Baldwin County, AL. All Rights Reserved.
          </p>
          <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground/60">
            <Link to="/privacy-policy" className="hover:text-primary transition-colors">
              Privacy Policy
            </Link>
            <span>|</span>
            <Link to="/sms-terms" className="hover:text-primary transition-colors">
              SMS Terms
            </Link>
            <span>|</span>
            <p>
              Website by{" "}
              <a
                href="https://www.heyblueheron.com/"
                target="_blank"
                rel="noopener"
                className="hover:text-primary transition-colors"
              >
                Blue Heron Marketing
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
